import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import Card from "../../../UI/Card/Card";
import Button from "../../../UI/Button/Button"
import ErrorPanel, { ErrorSize } from "../../../UI/ErrorPanel/ErrorPanel";

import { faCheckCircle, faTimesCircle, faUnlock, faLock } from "@fortawesome/free-solid-svg-icons";

import * as ApplicationConstant from "../../../Constants/ApplicationConstant";
import * as ApplicationsUtils from "../../../Utils/ApplicationsUtils";
import * as ErrorUtils from "../../../Utils/ErrorUtils";
import * as ApplicationsActions from "../../../../middleware/Store/Actions/ApplicationsActions";
import * as SandBoxApplicationsActions from "../../../../middleware/Store/Actions/SandBoxApplicationsActions";

import LogService from "../../../../middleware/CommonSdk/LogService";
const LOG_TAG = "APP-EDITION-ADMIN";

const Admin = (props) => {

    const intl = useIntl();
    const [reason, setReason] = useState("");
    const [application, setApplication] = useState(null);
    const [progressDecline, setProgressDecline] = useState(false);
    const [progress, setProgress] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        LogService.trace(LOG_TAG, "useEffect application");
        if (props.applicationId) {
            setApplication(ApplicationsUtils.getApplicationById(props.applicationId, props.applications));

            if (props.applicationId !== application?.id) {
                setProgress(false);
                setProgressDecline(false)
                setErrorMessage("");
            }
        }
    }, [props.applications, props.applicationId]);

    const handleDeploy = (event) => {
        LogService.trace(LOG_TAG, "handleDeploy", event.target.value);

        setProgress(true);
        props.deploy(application.id, reason)
            .then(() => {
                LogService.debug(LOG_TAG, "handleDeploy success");
                setProgress(false);
                if (props.onActionSuccess) {
                    props.onActionSuccess();
                }
            })
            .catch((errorPayload) => {
                LogService.debug(LOG_TAG, "handleDeploy error");
                setProgress(false);
                setErrorMessage(ErrorUtils.getPrettyErrorMessage(errorPayload, intl));
                if (props.onActionFailed) {
                    props.onActionFailed();
                }
            });
    };

    const handleDecline = (event) => {
        LogService.trace(LOG_TAG, "handleDecline", event.target.value);

        setProgressDecline(true);
        props.decline(application.id)
            .then(() => {
                LogService.debug(LOG_TAG, "handleDecline success");
                setProgressDecline(false);
                if (props.onActionSuccess) {
                    props.onActionSuccess();
                }
            })
            .catch((errorPayload) => {
                LogService.debug(LOG_TAG, "handleDecline error");
                setProgressDecline(false);
                setErrorMessage(ErrorUtils.getPrettyErrorMessage(errorPayload, intl));
                if (props.onActionFailed) {
                    props.onActionFailed();
                }
            });
    };

    const handleBlock = (event) => {
        LogService.trace(LOG_TAG, "handleBlock", event.target.value);

        setProgress(true);
        props.block(application.id)
            .then(() => {
                LogService.debug(LOG_TAG, "handleBlock success");
                setProgress(false);
                if (props.onActionSuccess) {
                    props.onActionSuccess();
                }
            })
            .catch((errorPayload) => {
                LogService.debug(LOG_TAG, "handleBlock error");
                setProgress(false);
                setErrorMessage(ErrorUtils.getPrettyErrorMessage(errorPayload, intl));
                if (props.onActionFailed) {
                    props.onActionFailed();
                }
            });
    };

    const handleUnblock = (event) => {
        LogService.trace(LOG_TAG, "handleUnblock", event.target.value);

        setProgress(true);
        props.unblock(application.id)
            .then(() => {
                LogService.debug(LOG_TAG, "handleUnblock success");
                setProgress(false);
                if (props.onActionSuccess) {
                    props.onActionSuccess();
                }
            })
            .catch((errorPayload) => {
                LogService.debug(LOG_TAG, "handleUnblock error");
                setProgress(false);
                setErrorMessage(ErrorUtils.getPrettyErrorMessage(errorPayload, intl));
                if (props.onActionFailed) {
                    props.onActionFailed();
                }
            });
    };

    const handleReasonChange = (event) => {
        setReason(event.target.value);
    }

    LogService.trace(LOG_TAG, "render");
    const disableDeployButton = !reason || reason.length === 0;

    let deployMessage = intl.formatMessage({ id: "applicationEditionAdminDeployMessage" }, { name: application?.name, b: chunks => <b>{chunks}</b> });

    let errorMessageDisplay = null;
    if (errorMessage) {
        errorMessageDisplay =
            <div className="mt-2">
                <ErrorPanel size={ErrorSize.Small} >{errorMessage}</ErrorPanel>
            </div>
    }

    let display = null;
    if (application?.env === ApplicationConstant.Env.InDeployment) {
        display =
            <Card title={intl.messages.applicationEditionAdminDeployTitle} header={props.showHeader}>
                <form>
                    <div className="form-group">
                        <label className="input-label">{intl.messages.applicationEditionAdminDeployReasonTitle}</label>
                        <input
                            value={reason}
                            type="text"
                            className="form-control"
                            autoComplete="off"
                            placeholder={intl.messages.applicationEditionAdminDeployReasonPlaceholder}
                            onChange={handleReasonChange}
                        />
                    </div>
                    <div className="panel bg-fog mb-3">{deployMessage}</div>
                    {props.isUpdateEnabled &&
                        <div>
                            <Button
                                label={intl.messages.applicationEditionAdminDeclineButton}
                                className="btn-danger mr-2"
                                icon={faTimesCircle}
                                onClick={handleDecline}
                                spinner={progressDecline}
                                disabled={progressDecline}
                            />
                            <Button
                                label={intl.messages.applicationEditionAdminDeployButton}
                                icon={faCheckCircle}
                                onClick={handleDeploy}
                                spinner={progress}
                                disabled={progress || disableDeployButton}
                                className="btn-submit"
                            />
                            {errorMessageDisplay}
                        </div>
                    }
                </form>
            </Card>
    } else if (application?.env === ApplicationConstant.Env.Deployed && application?.state === ApplicationConstant.State.Blocked) {
        display =
            <Card title={intl.messages.applicationEditionAdminBlockTitle} header={props.showHeader}>
                    <div className="panel bg-fog mb-3">{intl.messages.applicationEditionAdminBlockMessage}</div>
                {props.isUpdateEnabled &&
                    <div>
                        <Button
                            label={intl.messages.applicationEditionAdminUnblockButton}
                            icon={faUnlock}
                                className="btn-danger"
                            onClick={handleUnblock}
                            spinner={progress}
                            disabled={progress}
                        />
                        {errorMessageDisplay}
                    </div>
                }
            </Card>
    } else if (application?.env === ApplicationConstant.Env.Deployed) {
        display =
            <Card title={intl.messages.applicationEditionAdminBlockTitle} header={props.showHeader}>
                    <div className="panel bg-fog mb-3">{intl.messages.applicationEditionAdminBlockMessage}</div>
                {props.isUpdateEnabled &&
                    <div>
                        <Button
                            label={intl.messages.applicationEditionAdminBlockButton}
                            icon={faLock}
                                className="btn-danger"
                            onClick={handleBlock}
                            spinner={progress}
                            disabled={progress}
                        />
                        {errorMessageDisplay}
                    </div>
                }
            </Card>
    }

    return <div>{display}</div>;
}

Admin.propTypes = {
    applicationId: PropTypes.string,
    isUpdateEnabled: PropTypes.bool,
    onActionSuccess: PropTypes.func,
    onActionFailed: PropTypes.func,
    showHeader: PropTypes.bool,
    isSandBoxApplication: PropTypes.bool
};

Admin.defaultProps = {
    applicationId: "",
    isUpdateEnabled: false,
    onActionSuccess: null,
    onActionFailed: null,
    showHeader: true,
    isSandBoxApplication: false
}

const mapStateToProps = (state, props) => {
    return {
        applications: props.isSandBoxApplication ? state.sandBoxApplications.applications : state.applications.applications
    };
};

const mapDispatchToProps = (dispatch, props) => {
    return {
        deploy: (appId, reason) => dispatch(props.isSandBoxApplication ? SandBoxApplicationsActions.deployApplication(appId, reason) : ApplicationsActions.deployApplication(appId, reason)),
        decline: (appId) => dispatch(props.isSandBoxApplication ? SandBoxApplicationsActions.declineApplication(appId) : ApplicationsActions.declineApplication(appId)),
        block: (appId) => dispatch(props.isSandBoxApplication ? SandBoxApplicationsActions.blockApplication(appId) : ApplicationsActions.blockApplication(appId)),
        unblock: (appId) => dispatch(props.isSandBoxApplication ? SandBoxApplicationsActions.unblockApplication(appId) : ApplicationsActions.unblockApplication(appId))
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(Admin);